import { formatDate, SYSTEM_FALLBACK, wallClockToUtcIso } from '@zag/core/common';
import { dayKeyToUtcMs, MS_PER_DAY } from './day-key-math';

export interface PolicyWorkInterval {
  startMs: number;
  endMs: number;
  breakMinutes: number;
}

export interface DailyPiece extends PolicyWorkInterval {
  dateKey: string;
}

export function unionNetMinutes(intervals: readonly PolicyWorkInterval[]): number {
  const sorted = intervals.filter(interval => interval.endMs > interval.startMs).sort((a, b) => a.startMs - b.startMs);
  let unionMs = 0;
  let breakMinutes = 0;
  let cursorStartMs: number | null = null;
  let cursorEndMs = 0;
  for (const interval of sorted) {
    breakMinutes += interval.breakMinutes;
    if (cursorStartMs === null) {
      cursorStartMs = interval.startMs;
      cursorEndMs = interval.endMs;
      continue;
    }
    if (interval.startMs <= cursorEndMs) {
      cursorEndMs = Math.max(cursorEndMs, interval.endMs);
      continue;
    }
    unionMs += cursorEndMs - cursorStartMs;
    cursorStartMs = interval.startMs;
    cursorEndMs = interval.endMs;
  }
  if (cursorStartMs !== null) unionMs += cursorEndMs - cursorStartMs;
  return Math.max(0, unionMs / 60_000 - breakMinutes);
}

function dateKeyOfMs(utcMs: number, timeZone: string): string {
  if (timeZone === 'UTC') return new Date(utcMs).toISOString().slice(0, 10);
  return formatDate(utcMs, { ...SYSTEM_FALLBACK, locale: 'en-CA', timeZone });
}

function midnightMs(dateKey: string, timeZone: string): number {
  if (timeZone === 'UTC') return dayKeyToUtcMs(dateKey);
  return new Date(wallClockToUtcIso(dateKey, '00:00', { ...SYSTEM_FALLBACK, timeZone })).getTime();
}

export function dailyPiecesOf(row: { startTime: Date | null; endTime: Date | null; breakMinutes: number }, timeZone = 'UTC'): DailyPiece[] {
  if (!row.startTime || !row.endTime) return [];
  const startMs = row.startTime.getTime();
  const endMs = row.endTime.getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return [];
  const grossMs = endMs - startMs;
  const pieces: DailyPiece[] = [];
  let dateKey = dateKeyOfMs(startMs, timeZone);
  let dayStartMs = midnightMs(dateKey, timeZone);
  while (dayStartMs < endMs) {
    const nextDateKey = new Date(dayKeyToUtcMs(dateKey) + MS_PER_DAY).toISOString().slice(0, 10);
    const nextDayStartMs = midnightMs(nextDateKey, timeZone);
    const pieceStartMs = Math.max(startMs, dayStartMs);
    const pieceEndMs = Math.min(endMs, nextDayStartMs);
    pieces.push({
      dateKey,
      startMs: pieceStartMs,
      endMs: pieceEndMs,
      breakMinutes: row.breakMinutes * ((pieceEndMs - pieceStartMs) / grossMs),
    });
    dateKey = nextDateKey;
    dayStartMs = nextDayStartMs;
  }
  return pieces;
}

export function workedDatesOf(booking: { bookingDate: string; startTime: Date | null; endTime: Date | null }, timeZone = 'UTC'): string[] {
  if (!booking.startTime || !booking.endTime) return [booking.bookingDate];
  return dailyPiecesOf({ ...booking, breakMinutes: 0 }, timeZone).map(piece => piece.dateKey);
}
